import { useState } from 'react';
import { cn } from '../../lib/cn';
import { validateEmail } from '../../lib/forms';
import { useFormSubmit } from '../../hooks/useFormSubmit';
import { company } from '../../data/company';
import { Button } from './Button';
import styles from './Field.module.css';

/**
 * The inline "enter your email + Get Started" pill used by Request a Demo and
 * the newsletter. Client-side validation runs regardless of whether the backend
 * is reachable; a failed POST reports honestly instead of showing a fake tick.
 */
export function EmailCapture({
  onSubmit,
  placeholder = 'Enter your email',
  cta = 'Get Started',
  label = 'Email address',
  successMessage = 'Thanks — we’ll be in touch shortly.',
  className,
}) {
  const [email, setEmail] = useState('');
  const [touchedError, setTouchedError] = useState(null);
  const { status, error, channel, submit, isSubmitting, reset } = useFormSubmit(onSubmit);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const invalid = validateEmail(email);
    setTouchedError(invalid);
    if (invalid) return;
    const ok = await submit(email);
    if (ok) setEmail('');
  };

  const shownError = touchedError ?? error;

  return (
    <form className={className} onSubmit={handleSubmit} noValidate>
      <div className={cn(styles.capture, shownError && styles.captureInvalid)}>
        <label className="u-visually-hidden" htmlFor="email-capture">
          {label}
        </label>
        <input
          id="email-capture"
          type="email"
          inputMode="email"
          autoComplete="email"
          className={styles.input}
          placeholder={placeholder}
          value={email}
          aria-invalid={shownError ? true : undefined}
          aria-describedby={shownError ? 'email-capture-error' : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            if (touchedError) setTouchedError(null);
            if (status !== 'idle') reset();
          }}
        />
        <Button type="submit" size="md" disabled={isSubmitting}>
          {isSubmitting ? 'Sending…' : cta}
        </Button>
      </div>

      {shownError && (
        <p id="email-capture-error" className={cn(styles.message, styles.error)} role="alert">
          {shownError}{' '}
          {error && <a href={`mailto:${company.email}`}>{company.email}</a>}
        </p>
      )}
      {status === 'success' && (
        <p className={cn(styles.message, styles.success)} role="status">
          {channel === 'mailto'
            ? 'Your email app should have opened with the request ready — press send.'
            : successMessage}
        </p>
      )}
    </form>
  );
}
